import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Users,
  FileText,
  IndianRupee,
  Package,
  Database,
  Download
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { getRecentInvoices } from '../utils/db/invoices';
import { getRecentBookings } from '../utils/db/bookings';
import { supabase } from '../utils/supabaseClient';
import { getTotalCustomers } from '../utils/db/customers';
import { exportToExcel } from '../utils/exportData';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalCustomers: 0,
    totalEarnings: 0,
    totalBookings: 0,
    monthlyInvoices: 0,
    monthlyEarnings: 0,
    gstRevenue: 0,
    nonGstRevenue: 0
  });

  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    // Load dashboard data from database
    const loadDashboardData = async () => {
      if (!user) return;

      try {
        setIsLoading(true);

        // Fetch recent invoices
        const invoices = await getRecentInvoices(user.id);
        setRecentInvoices(invoices);

        // Fetch recent bookings (limited list for UI)
        const bookings = await getRecentBookings(user.id);
        setRecentBookings(bookings);

        // Fetch total bookings count accurately (not limited)
        const { count: totalBookingsCount, error: totalBookingsError } = await supabase
          .from('bookings')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);
        if (totalBookingsError) throw totalBookingsError;

        // Fetch total customers
        const customerCount = await getTotalCustomers(user.id);

        // Fetch all invoices for accurate stats (count and earnings)
        const { data: allInvoices, error: statsError } = await supabase
          .from('invoices')
          .select('grand_total, invoice_date, invoice_no')
          .eq('user_id', user.id);

        if (statsError) throw statsError;

        // Calculate stats from all invoices
        const totalInvoices = allInvoices.length;
        const totalEarnings = allInvoices.reduce((sum, invoice) => sum + (invoice.grand_total || 0), 0);

        // Split revenue into GST and Non-GST
        const gstRevenue = allInvoices
          .filter(inv => !inv.invoice_no?.startsWith('NG-'))
          .reduce((sum, inv) => sum + (inv.grand_total || 0), 0);

        const nonGstRevenue = allInvoices
          .filter(inv => inv.invoice_no?.startsWith('NG-'))
          .reduce((sum, inv) => sum + (inv.grand_total || 0), 0);

        // Calculate monthly stats
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();

        const currentMonthInvoices = allInvoices.filter(invoice => {
          const invoiceDate = new Date(invoice.invoice_date);
          return invoiceDate.getMonth() === currentMonth &&
            invoiceDate.getFullYear() === currentYear;
        });

        const monthlyInvoices = currentMonthInvoices.length;
        const monthlyEarnings = currentMonthInvoices.reduce((sum, invoice) => sum + (invoice.grand_total || 0), 0);

        setStats({
          totalInvoices,
          totalCustomers: customerCount,
          totalEarnings,
          totalBookings: totalBookingsCount || 0,
          monthlyInvoices,
          monthlyEarnings,
          gstRevenue,
          nonGstRevenue
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [user]);

  const handleExportData = async (dataType = 'all') => {
    if (!user) return;

    try {
      setIsExporting(true);
      await exportToExcel(user.id, dataType);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, change }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          {change && (
            <p className="text-sm text-green-600 flex items-center">
              <TrendingUp className="h-4 w-4 mr-1" />
              {change}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Overview of your franchise billing system</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total GST Revenue"
          value={`₹${stats.gstRevenue.toLocaleString()}`}
          icon={IndianRupee}
          color="bg-primary-500"
          change={null}
        />
        <StatCard
          title="Total Non-GST Revenue"
          value={`₹${stats.nonGstRevenue.toLocaleString()}`}
          icon={IndianRupee}
          color="bg-green-500"
        />
        <StatCard
          title="Total Earnings"
          value={stats.totalEarnings > 0 ? `₹${stats.totalEarnings.toLocaleString()}` : '₹0'}
          icon={IndianRupee}
          color="bg-yellow-500"
          change={stats.monthlyEarnings > 0 ? `₹${stats.monthlyEarnings.toLocaleString()} this month` : null}
        />
        <StatCard
          title="Total Bookings"
          value={stats.totalBookings}
          icon={Package}
          color="bg-purple-500"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Invoices</h3>
          </div>
          <div className="p-6">
            {recentInvoices.length > 0 ? (
              <div className="space-y-4">
                {recentInvoices.map((invoice, index) => (
                  <div key={invoice.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Invoice #{invoice.invoice_number}</p>
                      <p className="text-sm text-gray-600">{invoice.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">₹{invoice.grand_total?.toLocaleString()}</p>
                      <p className="text-sm text-gray-600">{new Date(invoice.invoice_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No invoices generated yet</p>
                <button
                  onClick={() => navigate('/invoice-generator')}
                  className="mt-3 text-sm text-primary-600 hover:text-primary-800 font-medium"
                >
                  Generate your first invoice
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Bookings</h3>
          </div>
          <div className="p-6">
            {recentBookings.length > 0 ? (
              <div className="space-y-4">
                {recentBookings.map((booking, index) => (
                  <div key={booking.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">AWB #{booking.awb_no}</p>
                      <p className="text-sm text-gray-600">{booking.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">₹{booking.amount?.toLocaleString()}</p>
                      <p className="text-sm text-gray-600">{new Date(booking.booking_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No bookings added yet</p>
                <button
                  onClick={() => navigate('/booking')}
                  className="mt-3 text-sm text-primary-600 hover:text-primary-800 font-medium"
                >
                  Add your first booking
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/booking')}
              className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <Package className="h-6 w-6 mr-2 text-gray-400" />
              <span className="text-gray-600">Add New Booking</span>
            </button>
            <button
              onClick={() => navigate('/invoice-generator')}
              className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <FileText className="h-6 w-6 mr-2 text-gray-400" />
              <span className="text-gray-600">Generate Invoice</span>
            </button>
            <button
              onClick={() => navigate('/customers')}
              className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <Users className="h-6 w-6 mr-2 text-gray-400" />
              <span className="text-gray-600">Add Customer</span>
            </button>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Database className="h-5 w-5 mr-2" />
            Data Management
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-primary-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-primary-900 mb-2">Export Data</h4>
              <p className="text-sm text-primary-700 mb-3">Download your data in Excel format for backup or reporting</p>
              <button
                onClick={() => handleExportData('all')}
                disabled={isExporting}
                className="flex items-center w-full px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <Download className="h-5 w-5 mr-2" />
                {isExporting ? 'Exporting...' : 'Export All Data'}
              </button>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  onClick={() => handleExportData('invoices')}
                  disabled={isExporting}
                  className="flex items-center justify-center px-2 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-md hover:bg-primary-200"
                >
                  Invoices Only
                </button>
                <button
                  onClick={() => handleExportData('bookings')}
                  disabled={isExporting}
                  className="flex items-center justify-center px-2 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-md hover:bg-primary-200"
                >
                  Bookings Only
                </button>
              </div>
            </div>
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Data Summary</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Total Invoices:</span>
                    <span className="font-medium">{stats.totalInvoices}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Total Bookings:</span>
                    <span className="font-medium">{stats.totalBookings}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Total Earnings:</span>
                    <span className="font-medium">₹{stats.totalEarnings.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;